import type { ReactNode } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { Button, EmptyState, SkeletonList } from '@/components/ui'
import { ApiError } from '@/internationalization/api-client'
import { useT } from '@/internationalization/i18n'

export interface QueryBoundaryProps {
  isLoading: boolean
  error: unknown
  /** True when the request succeeded and returned nothing. */
  isEmpty?: boolean
  empty?: ReactNode
  onRetry?: () => void
  skeleton?: ReactNode
  children: ReactNode
}

/**
 * The loading / error / empty triad, in one place.
 *
 * Screens that hand-roll these three states diverge immediately: one shows a
 * spinner, another a blank page, a third an unhandled exception. The error copy
 * distinguishes "we could not reach the server" (retryable) from "the server
 * refused this" (not), because telling someone to retry a 403 wastes their time.
 */
export function QueryBoundary({
  isLoading,
  error,
  isEmpty,
  empty,
  onRetry,
  skeleton,
  children,
}: QueryBoundaryProps) {
  const t = useT()
  if (isLoading) return <>{skeleton ?? <SkeletonList rows={6} className="p-4" />}</>

  if (error) {
    const apiError = error instanceof ApiError ? error : null
    const retryable = apiError?.isTransient ?? true
    return (
      <EmptyState
        icon={<AlertTriangle />}
        title={retryable ? t('query.loadFailTitle') : t('query.noAccessTitle')}
        description={
          apiError?.message ??
          (retryable ? t('query.loadFailBody') : t('query.noAccessBody'))
        }
        action={
          retryable && onRetry ? (
            <Button variant="primary" leftIcon={<RefreshCw className="h-4 w-4" />} onClick={onRetry}>
              {t('common.tryAgain')}
            </Button>
          ) : undefined
        }
      />
    )
  }

  if (isEmpty) return <>{empty ?? <EmptyState title={t('query.nothingHere')} />}</>

  return <>{children}</>
}
